/* ==========================================================================
   ProTan USA accessibility fixes (WCAG 2.1 / 2.2 Level AA)
   Loaded after theme.js. Covers behaviour that lives in third-party apps
   (Rebuy Smart Cart, ShopPad Infinite Options) plus a few theme gaps.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;

  function onReady(fn) {
    if (doc.readyState !== 'loading') { fn(); } else { doc.addEventListener('DOMContentLoaded', fn); }
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusablesIn(root) {
    var sel = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(root.querySelectorAll(sel), isVisible);
  }

  /* ------------------------------------------------------------------------
     1. Mobile menu drawer - complete focus trap (WCAG 2.4.3)
     The theme only wrapped forward Tab; Shift+Tab escaped into the page.
     ------------------------------------------------------------------------ */
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var drawer = doc.querySelector('#NavDrawer.drawer--is-open');
    if (!drawer) return;
    var items = focusablesIn(drawer);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;
    if (!drawer.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && (active === first || active === drawer)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, true);

  /* ------------------------------------------------------------------------
     2. Rebuy Smart Cart (third-party app)
     - While closed it stays in the tab order off-screen (aria-hidden="true"
       but focusable): keyboard users land on invisible buttons. -> inert.
     - When it opens, focus stays on the page behind it. -> move focus in.
     - When it closes, focus drops to <body>. -> return it to the trigger.
     (WCAG 2.4.3, 2.4.7, 4.1.2)
     ------------------------------------------------------------------------ */
  var lastTrigger = null;

  function rememberTrigger(e) {
    var cart = doc.getElementById('rebuy-cart');
    if (cart && cart.contains(e.target)) return;
    var t = e.target && e.target.closest ? e.target.closest('a[href], button, input, select, [tabindex]') : null;
    if (t) lastTrigger = t;
  }
  doc.addEventListener('focusin', rememberTrigger, true);
  doc.addEventListener('pointerdown', rememberTrigger, true);

  function wireRebuyCart(cart) {
    if (cart.__adaWired) return;
    cart.__adaWired = true;
    var wasOpen = false;

    function sync() {
      var closed = cart.getAttribute('aria-hidden') === 'true';
      if (closed) {
        cart.setAttribute('inert', '');
        if (wasOpen) {
          wasOpen = false;
          var target = (lastTrigger && doc.contains(lastTrigger) && isVisible(lastTrigger))
            ? lastTrigger
            : doc.querySelector('.js-drawer-open-cart');
          if (target) { setTimeout(function () { target.focus(); }, 50); }
        }
      } else {
        cart.removeAttribute('inert');
        if (!wasOpen) {
          wasOpen = true;
          setTimeout(function () {
            if (cart.contains(doc.activeElement)) return;
            var close = cart.querySelector('[aria-label*="Close" i], .rebuy-cart__flyout-close');
            var target = close || focusablesIn(cart)[0];
            if (target) target.focus();
          }, 400);
        }
      }
    }

    new MutationObserver(sync).observe(cart, { attributes: true, attributeFilter: ['aria-hidden', 'class'] });
    sync();
  }

  /* ------------------------------------------------------------------------
     3. ShopPad Infinite Options (third-party app)
     Its field IDs contain spaces ("Desired First Appointment Time-0-0"), so
     aria-labelledby points at four non-existent IDs and the radio group has
     no name. Give each group a direct aria-label instead. (WCAG 1.3.1, 4.1.2)
     ------------------------------------------------------------------------ */
  function fixInfiniteOptions(root) {
    root.querySelectorAll('[aria-labelledby]').forEach(function (el) {
      var ref = el.getAttribute('aria-labelledby');
      if (!/\s/.test(ref)) return;
      var labelEl = doc.getElementById(ref);
      if (labelEl && labelEl.textContent.trim()) {
        el.setAttribute('aria-label', labelEl.textContent.trim());
        el.removeAttribute('aria-labelledby');
      }
    });
  }

  /* ------------------------------------------------------------------------
     4. Header cart link: keep the item count in its accessible name when
     Rebuy or the theme updates the cart without a page load. (WCAG 1.3.1)
     ------------------------------------------------------------------------ */
  var countTimer = null;
  function refreshCartCount() {
    clearTimeout(countTimer);
    countTimer = setTimeout(function () {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var n = cart.item_count || 0;
          var text = n > 0 ? ', ' + n + (n === 1 ? ' item' : ' items') : '';
          doc.querySelectorAll('[data-cart-count-a11y]').forEach(function (el) { el.textContent = text; });
        })
        .catch(function () {});
    }, 300);
  }
  ['rebuy:cart.change', 'rebuy:cart.add', 'rebuy:smartcart.hide', 'ajaxProduct:added', 'cart:updated'].forEach(function (evt) {
    doc.addEventListener(evt, refreshCartCount);
  });

  /* ------------------------------------------------------------------------
     5. Athletes page fallback (content fix is in the report): the athlete
     names were aria-hidden and the photos had no alt, so screen readers
     heard nothing. Names are visible next to each photo, so the photo is
     marked decorative and the name is exposed. (WCAG 1.1.1, 1.3.1)
     ------------------------------------------------------------------------ */
  function fixAthletes() {
    var wrap = doc.querySelector('.pt-athletes');
    if (!wrap) return;
    wrap.querySelectorAll('.grid-view-item').forEach(function (item) {
      var img = item.querySelector('img');
      var name = item.querySelector('.grid-view-item__title');
      if (img && !img.hasAttribute('alt')) img.setAttribute('alt', '');
      if (name) name.removeAttribute('aria-hidden');
    });
  }

  /* ------------------------------------------------------------------------
     6. Contact form: after submit the page reloads; move focus to the
     success or error message so it is announced. (WCAG 3.3.1, 4.1.3)
     ------------------------------------------------------------------------ */
  function focusFormMessage() {
    var msg = doc.querySelector('[data-form-message]');
    if (msg) { setTimeout(function () { msg.focus(); }, 300); }
  }

  /* ------------------------------------------------------------------------
     Boot + watch for app widgets that render after page load
     ------------------------------------------------------------------------ */
  onReady(function () {
    fixAthletes();
    focusFormMessage();
    fixInfiniteOptions(doc);

    var existing = doc.getElementById('rebuy-cart');
    if (existing) wireRebuyCart(existing);

    var bubble = doc.querySelector('.cart-link__bubble');
    if (bubble) {
      new MutationObserver(refreshCartCount).observe(bubble, { attributes: true, attributeFilter: ['class'] });
    }

    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        var cart = doc.getElementById('rebuy-cart');
        if (cart) wireRebuyCart(cart);
        var io = doc.getElementById('infiniteoptions-container');
        if (io) fixInfiniteOptions(io);
      }, 150);
    }).observe(doc.body, { childList: true, subtree: true });
  });
})();
